import React, { useMemo, useState } from 'react';
import PickerPopover from './PickerPopover';

/**
 * Picks the course a month is planned from, out of the courses this batch group
 * is actually enrolled in.
 *
 * A button and a list rather than a `<select>`, matching the Chapter picker
 * beside it: the whole field opens the list, and a course that only some of the
 * group's batches hold says so on its own row instead of hiding the coverage in
 * a suffix.
 */
const CoursePicker = ({ courses, selectedCourseId, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedCourse = useMemo(
    () => courses.find((course) => String(course.id) === String(selectedCourseId)) || null,
    [courses, selectedCourseId]
  );

  const needle = search.trim().toLowerCase();
  const visibleCourses = useMemo(
    () => (needle
      ? courses.filter((course) => (course.title || '').toLowerCase().includes(needle))
      : courses),
    [courses, needle]
  );

  const choose = (course) => {
    onSelect(course);
    setOpen(false);
    setSearch('');
  };

  return (
    <PickerPopover
      label="Course"
      value={selectedCourse ? selectedCourse.title : ''}
      placeholder={courses.length === 0 ? 'No courses on this group' : 'Select a course…'}
      open={open}
      onOpenChange={setOpen}
      disabled={courses.length === 0}
    >
      {courses.length > 5 && (
        <input
          type="search"
          className="sb-pickersearch"
          value={search}
          placeholder={`Search ${courses.length} courses…`}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Search courses"
        />
      )}

      {visibleCourses.length === 0 ? (
        <span className="cal-empty">No course matches “{search}”.</span>
      ) : (
        <ul className="sb-pickerlist" role="listbox">
          {visibleCourses.map((course) => {
            const isSelected = String(course.id) === String(selectedCourseId);
            return (
              <li key={course.id}>
                <button
                  type="button"
                  className={`sb-pickeroption${isSelected ? ' selected' : ''}`}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => choose(course)}
                >
                  <span className="sb-pickeroption-title">{course.title}</span>
                  {/* The syllabus course is what a month is planned from; the
                      rest are add-ons the batches also happen to hold. */}
                  {course.isPrimary && <span className="sb-pickeroption-tag">syllabus</span>}
                  {!course.onEveryBatch && (
                    <span className="sb-pickeroption-note">
                      {course.batchCount} of {course.totalBatches} batches
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </PickerPopover>
  );
};

export default CoursePicker;
